import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { WEALTH_SERVICES, RISK_SERVICES, FINANCE_SERVICES } from '../data/siteData';
import './ServiceDetail.css';
import Icon from '../components/Icon';

const TYPE_META = {
  wealth: {
    label: 'Wealth Management',
    color: '#1B6B2E',
    bgColor: 'rgba(27,107,46,0.06)',
    path: '/wealth-management',
    services: WEALTH_SERVICES,
    icon: '📈',
    desc: 'Grow and protect your capital with expert investment strategies.',
  },
  risk: {
    label: 'Risk Management',
    color: '#C0201F',
    bgColor: 'rgba(192,32,31,0.06)',
    path: '/risk-management',
    services: RISK_SERVICES,
    icon: '🛡️',
    desc: 'Protect your family and assets against life\'s uncertainties.',
  },
  finance: {
    label: 'Finance Management',
    color: '#1a56a0',
    bgColor: 'rgba(26,86,160,0.06)',
    path: '/finance-management',
    services: FINANCE_SERVICES,
    icon: '🏦',
    desc: 'Smart loan and financing solutions tailored for your goals.',
  },
};

const BENEFITS = {
  wealth: [
    { icon: '🎯', title: 'Goal-Based Strategy', desc: 'Every portfolio is designed around your life milestones — retirement, education, wealth creation.' },
    { icon: '📊', title: 'Research-Backed', desc: 'All recommendations backed by deep fundamental and technical analysis by seasoned experts.' },
    { icon: '🔐', title: 'SEBI Compliant', desc: 'Investments held directly in your name. Fully regulated, transparent, and compliant.' },
    { icon: '🤝', title: 'Dedicated Advisor', desc: 'A personal relationship with G.M. Narendra — someone who knows your financial story.' },
  ],
  risk: [
    { icon: '🛡️', title: 'Comprehensive Coverage', desc: 'We compare plans across leading insurers to ensure you get maximum cover at the best premium.' },
    { icon: '⚡', title: 'Fast Claims Support', desc: 'Our team guides you through every step of claim filing for a smooth, stress-free experience.' },
    { icon: '👨‍👩‍👧‍👦', title: 'Family Protection', desc: 'Tailored plans covering your entire family — parents, spouse, children — under one roof.' },
    { icon: '📋', title: 'Needs-Based Selection', desc: 'We assess your risk profile and recommend only policies that truly match your needs.' },
  ],
  finance: [
    { icon: '🏦', title: 'Best Rates', desc: 'We compare offers from 20+ banks and NBFCs to get you the most competitive interest rates.' },
    { icon: '⚡', title: 'Fast Processing', desc: 'Streamlined application assistance ensures quick approval and speedy disbursal.' },
    { icon: '📋', title: 'Minimal Documentation', desc: 'We simplify the paperwork so you can focus on what matters — your goals.' },
    { icon: '🤝', title: 'End-to-End Support', desc: "From application to sanction to disbursal, we're with you at every step." },
  ],
};

export default function ServiceDetail() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const meta = TYPE_META[type];
  if (!meta) { navigate('/'); return null; }
  const service = meta.services.find(s => s.id === id);
  if (!service) { navigate(meta.path); return null; }
  const otherServices = meta.services.filter(s => s.id !== id);
  const benefits = BENEFITS[type];
  
  // Create the service name for the query parameter
  const serviceName = encodeURIComponent(service.short || service.name);

  return (
    <div className="sd-page page-wrapper">
      {/* Breadcrumb */}
      <div className="sd-breadcrumb" style={{ borderColor: meta.color }}>
        <div className="sd-bc-inner">
          <Link to="/" className="bc-link">Home</Link>
          <span className="bc-sep">›</span>
          <Link to={meta.path} className="bc-link" style={{ color: meta.color }}>{meta.label}</Link>
          <span className="bc-sep">›</span>
          <span className="bc-current">{service.short || service.name}</span>
        </div>
      </div>

      {/* Hero */}
      <div className="sd-hero" style={{ background: `linear-gradient(135deg, ${meta.color}10, ${meta.color}05)` }}>
        <div className="sd-hero-inner container">
          <div className="sd-hero-left">
            <div className="sd-category" style={{ color: meta.color, borderColor: meta.color }}>
              <Icon icon={meta.icon} size={18} /> {meta.label}
            </div>
            <h1 className="sd-title">{service.name}</h1>
            <p className="sd-tagline">{service.tagline}</p>
            <p className="sd-desc">{service.desc}</p>
            <div className="sd-actions">
              {/* FIXED: Get Started button now passes the service name */}
              <Link 
                to={`/contact?service=${serviceName}`} 
                className="btn-primary"
              >
                Get Started →
              </Link>
              <a href="tel:9849131273" className="btn-ghost">📞 Call Now</a>
            </div>
            {service.minInvestment && (
              <div className="sd-min-invest" style={{ borderColor: meta.color }}>
                <span className="smi-label">Minimum Investment</span>
                <span className="smi-value" style={{ color: meta.color }}>{service.minInvestment}</span>
              </div>
            )}
          </div>
          <div className="sd-hero-right">
            <div className="sd-icon-card" style={{ background: meta.bgColor, borderColor: `${meta.color}30` }}>
              <div className="sd-big-icon"><Icon icon={service.icon} size={64} /></div>
              <div className="sd-icon-name">{service.short || service.name}</div>
              {service.riskLevel && (
                <div className="sd-risk-row">
                  <span className="sdr-label">Risk Level</span>
                  <span className="sdr-value" style={{ color: meta.color }}>{service.riskLevel}</span>
                </div>
              )}
              <div className="sd-cta-card">
                <div className="sdcc-label">Ready to invest?</div>
                {/* FIXED: Free Consultation button also passes the service name */}
                <Link 
                  to={`/contact?service=${serviceName}`} 
                  className="btn-primary" 
                  style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                >
                  Free Consultation →
                </Link>
                <a href="tel:9849131273" className="sdcc-phone">📞 9849131273</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <section className="sd-features-sec">
        <div className="container">
          <div className="sd-sec-label" style={{ color: meta.color }}>What's Included</div>
          <h2 className="sd-sec-title">Key Features of <span style={{ color: meta.color }}>{service.short || service.name}</span></h2>
          <div className="sd-features-grid">
            {service.features.map((f, i) => (
              <div key={i} className="sd-feature-item" style={{ borderColor: `${meta.color}25` }}>
                <div className="sdf-dot" style={{ background: meta.color }} />
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why LMS for this */}
      <section className="sd-why-sec" style={{ background: `${meta.color}08` }}>
        <div className="container">
          <div className="sd-sec-label" style={{ color: meta.color }}>Why Choose LMS</div>
          <h2 className="sd-sec-title">Our Edge in <span style={{ color: meta.color }}>{meta.label}</span></h2>
          <div className="sd-why-grid">
            {benefits.map((b, i) => (
              <div key={i} className="sd-why-card">
                <div className="sdwc-icon"><Icon icon={b.icon} size={40} /></div>
                <h4 className="sdwc-title">{b.title}</h4>
                <p className="sdwc-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="sd-process-sec">
        <div className="container">
          <div className="sd-sec-label" style={{ color: meta.color }}>How It Works</div>
          <h2 className="sd-sec-title">Getting Started is <span style={{ color: meta.color }}>Simple</span></h2>
          <div className="sd-process-grid">
            {[
              { num: '01', icon: '📞', title: 'Free Consultation', desc: 'Call or WhatsApp us. Discuss your needs, goals and current situation — no obligation.' },
              { num: '02', icon: '📋', title: 'Personalised Plan', desc: 'We design a plan specifically for your profile, budget and timeline.' },
              { num: '03', icon: '⚡', title: 'Quick Execution', desc: 'Seamless paperwork, KYC, and setup — we handle it all for you.' },
              { num: '04', icon: '📊', title: 'Ongoing Support', desc: 'Regular reviews, updates and advice throughout your journey.' },
            ].map((s, i) => (
              <div key={i} className="sd-proc-card">
                <div className="sdpc-num" style={{ color: meta.color }}>{s.num}</div>
                <div className="sdpc-icon"><Icon icon={s.icon} size={40} /></div>
                <h4 className="sdpc-title">{s.title}</h4>
                <p className="sdpc-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other services in same category */}
      {otherServices.length > 0 && (
        <section className="sd-related-sec" style={{ background: `${meta.color}06` }}>
          <div className="container">
            <div className="sd-sec-label" style={{ color: meta.color }}>More in {meta.label}</div>
            <h2 className="sd-sec-title">Explore Other <span style={{ color: meta.color }}>Services</span></h2>
            <div className="sd-related-grid">
              {otherServices.slice(0, 4).map(s => (
                <Link key={s.id} to={`/service/${type}/${s.id}`} className="sd-related-card">
                  <Icon icon={s.icon} className="sdr-icon" size={28} />
                  <div>
                    <div className="sdr-name">{s.short || s.name}</div>
                    <div className="sdr-tag">{s.tagline}</div>
                  </div>
                </Link>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Link to={meta.path} className="btn-ghost" style={{ borderColor: meta.color, color: meta.color }}>
                View All {meta.label} →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="sd-cta-sec" style={{ background: `linear-gradient(135deg, ${meta.color}, ${meta.color}bb)` }}>
        <div className="container sd-cta-inner">
          <h2 className="sd-cta-title">Ready to get started with {service.short || service.name}?</h2>
          <p className="sd-cta-sub">Speak with G.M. Narendra — Hyderabad's trusted management consultant — for a free, no-obligation consultation today.</p>
          <div className="sd-cta-actions">
            <a href="tel:9849131273" className="btn-white">📞 Call 9849131273</a>
            {/* FIXED: Schedule Meeting button also passes the service name */}
            <Link to={`/contact?service=${serviceName}`} className="btn-ghost-white">
              Schedule Meeting →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}